import React, { useCallback, useMemo, useRef, useState } from 'react';
import ChevronLeft from '@uber/base-icons/chevron-left';
import ChevronRight from '@uber/base-icons/chevron-right';
import PlayerNext from '@uber/base-icons/player-next';
import PlayerPauseFilled from '@uber/base-icons/player-pause';
import PlayerPlayFilled from '@uber/base-icons/player-play-filled';
import PlayerPrevious from '@uber/base-icons/player-previous';
import { useStyletron } from 'baseui';
import { LabelSmall } from 'baseui/typography';
import throttle from 'lodash/throttle';
import { useTrackInteractionLatency } from '../../../../common/hooks/use-track-interaction-latency';
import { IconButton } from '../../../../common/player-controls/icon-button';
import { ShortcutTooltip } from '../../../../common/player-controls/shortcut-tooltip';
import useAnalyticsForVideoLabelling from '../../../labelling/components/frames-control-panel/analytics/useAnalyticsForVideoLabelling';
import { PLATFORM_MOD_LABEL } from '../../../labelling/providers/utils/shortcut';
import type { VideoPlayerControlTypesT } from '../../../labelling/types';
import { TimeDisplayMode } from '../../../types';
import { getFrameToTime } from '../../../utils/frame-time';
import { PERFORMANCE_METRIC_EVENTS } from '../../constants/performance';
import { usePerformanceMetrics } from '../../hooks/performance/use-performance-metrics';
import { usePlaybackShortcuts } from '../../hooks/use-playback-shortcuts';
import { executePlaybackAction } from '../../utils/execute-playback-action';
import { BUTTON_SIZE, FRAME_SLIDER_WIDTH, LABELS, VIDEO_PLAYER_CONTROL_TYPES } from './constants';
import { FrameSlider } from './frame-slider';
type PlaybackControlsProps = {
  isPlaying: boolean;
  togglePlayback: (playbackStatus: boolean) => void;
  currentFrame: number;
  maxFrames: number;
  imageLoadStatus: boolean[];
  onFrameChange: (frame: number) => void;
  isVideoPlayer: boolean;
  isDisabled?: boolean;
  isMultiVideoIndex: boolean;
  mediaElement: HTMLVideoElement | null;
  timeDisplayMode?: TimeDisplayMode;
  frameRate?: number;
};

// We may move this to common in future if we need it in other places.
export const PlaybackControls = ({
  isPlaying,
  togglePlayback,
  currentFrame: currentFrameProp,
  maxFrames,
  imageLoadStatus,
  onFrameChange,
  isVideoPlayer,
  isDisabled,
  isMultiVideoIndex,
  mediaElement,
  timeDisplayMode,
  frameRate
}: PlaybackControlsProps) => {
  const [css, theme] = useStyletron();
  const [sliderValue, setSliderValue] = useState<[number]>([currentFrameProp]);
  const isSliderDragging = useRef(false);
  const throttledOnFrameChange = useMemo(() => throttle(onFrameChange, 33, {
    leading: true,
    trailing: true
  }), [onFrameChange]);
  const [prevFrame, setPrevFrame] = useState(currentFrameProp);
  if (currentFrameProp !== prevFrame) {
    setPrevFrame(currentFrameProp);
    setSliderValue([currentFrameProp]);
  }
  const isCurrentFrameLoaded = imageLoadStatus[currentFrameProp];
  const isNextFrameLoaded = currentFrameProp + 1 < imageLoadStatus.length ? imageLoadStatus[currentFrameProp + 1] : true;
  const isFrameLoaded = isCurrentFrameLoaded && isNextFrameLoaded;
  const showPlayPauseButton = isFrameLoaded || isVideoPlayer;
  const {
    sendEventVideoPlayerControls
  } = useAnalyticsForVideoLabelling();
  const {
    captureLatency
  } = usePerformanceMetrics();
  const stopPlaying = useTrackInteractionLatency(() => togglePlayback(false), mediaElement, {
    metricName: PERFORMANCE_METRIC_EVENTS.VIDEO_PAUSE_TO_PAINT,
    event: 'pause',
    captureLatency
  });
  const startPlaying = useTrackInteractionLatency(() => togglePlayback(true), mediaElement, {
    metricName: PERFORMANCE_METRIC_EVENTS.VIDEO_PLAY_TO_PAINT,
    event: 'play',
    captureLatency
  });
  const pausePlaybackIfPlaying = useCallback(() => {
    if (isPlaying) stopPlaying();
  }, [isPlaying, stopPlaying]);
  const handleSliderChange = useCallback(({
    value
  }: {
    value: [number];
  }) => {
    isSliderDragging.current = true;
    setSliderValue(value);
    throttledOnFrameChange(value[0]);
  }, [throttledOnFrameChange]);
  const handleSliderFinal = useCallback(({
    value
  }: {
    value: [number];
  }) => {
    isSliderDragging.current = false;
    setSliderValue(value);
    throttledOnFrameChange.cancel();
    onFrameChange(value[0]);
  }, [throttledOnFrameChange, onFrameChange]);
  const handlePlaybackControlsClick = useCallback((controlType: VideoPlayerControlTypesT) => {
    pausePlaybackIfPlaying();
    sendEventVideoPlayerControls({
      action: controlType
    });
    executePlaybackAction({
      controlType,
      currentFrame: currentFrameProp,
      maxFrames,
      onFrameChange,
      startPlaying
    });
  }, [pausePlaybackIfPlaying, currentFrameProp, onFrameChange, maxFrames, startPlaying, sendEventVideoPlayerControls]);
  usePlaybackShortcuts({
    togglePlayback,
    isPlaying,
    isDisabled,
    isMultiVideoIndex,
    handlePlaybackControlsClick
  });
  const isTimeMode = timeDisplayMode === TimeDisplayMode.TIMESTAMP;
  const sliderLabel = isTimeMode ? `${getFrameToTime({
    frame: currentFrameProp,
    maxFrames,
    frameRate
  })} / ${getFrameToTime({
    frame: maxFrames - 1,
    maxFrames,
    frameRate
  })}` : `${currentFrameProp + 1} / ${maxFrames}`;
  return <div className={css({
    display: 'flex',
    alignItems: 'center',
    gap: theme.sizing.scale300
  })}>
      <div className={css({
      display: 'flex',
      gap: theme.sizing.scale300
    })}>
        {!isMultiVideoIndex && <ShortcutTooltip label={LABELS.STARTING_FRAME} shortcutKeys={[PLATFORM_MOD_LABEL, '←']}>
            <IconButton data-testid="start-frame" aria-label={LABELS.STARTING_FRAME} onClick={() => !isDisabled && handlePlaybackControlsClick(VIDEO_PLAYER_CONTROL_TYPES.SKIP_TO_FIRST)} disabled={isDisabled}>
              <PlayerPrevious size={BUTTON_SIZE} />
            </IconButton>
          </ShortcutTooltip>}
        {!isMultiVideoIndex && <ShortcutTooltip label={LABELS.PREVIOUS_FRAME} shortcutKeys={['m']}>
            <IconButton data-testid="prev-frame" aria-label={LABELS.PREVIOUS_FRAME} onClick={e => {
          const type: VideoPlayerControlTypesT = (e.nativeEvent as MouseEvent).shiftKey ? VIDEO_PLAYER_CONTROL_TYPES.JUMP_BACKWARD : VIDEO_PLAYER_CONTROL_TYPES.BACKWARD;
          !isDisabled && handlePlaybackControlsClick(type);
        }} disabled={isDisabled}>
              <ChevronLeft size={BUTTON_SIZE} />
            </IconButton>
          </ShortcutTooltip>}
        {showPlayPauseButton ? <ShortcutTooltip label={isPlaying ? LABELS.PAUSE : LABELS.PLAY} shortcutKeys={['Space']}>
            <IconButton data-testid="toggle-play-btn" aria-label={isPlaying ? LABELS.PAUSE : LABELS.PLAY} onClick={() => !isDisabled && handlePlaybackControlsClick(isPlaying ? VIDEO_PLAYER_CONTROL_TYPES.PAUSE : VIDEO_PLAYER_CONTROL_TYPES.PLAY)} disabled={isDisabled}>
              {isPlaying ? <PlayerPauseFilled size={BUTTON_SIZE} /> : <PlayerPlayFilled size={BUTTON_SIZE} />}
            </IconButton>
          </ShortcutTooltip> : <IconButton isLoading />}
        {!isMultiVideoIndex && <ShortcutTooltip label={LABELS.NEXT_FRAME} shortcutKeys={['n']}>
            <IconButton data-testid="next-frame" aria-label={LABELS.NEXT_FRAME} onClick={e => {
          const type: VideoPlayerControlTypesT = (e.nativeEvent as MouseEvent).shiftKey ? VIDEO_PLAYER_CONTROL_TYPES.JUMP_FORWARD : VIDEO_PLAYER_CONTROL_TYPES.FORWARD;
          !isDisabled && handlePlaybackControlsClick(type);
        }} disabled={isDisabled}>
              <ChevronRight size={BUTTON_SIZE} />
            </IconButton>
          </ShortcutTooltip>}
        {!isMultiVideoIndex && <ShortcutTooltip label={LABELS.LAST_FRAME} shortcutKeys={[PLATFORM_MOD_LABEL, '→']}>
            <IconButton data-testid="last-frame" aria-label={LABELS.LAST_FRAME} onClick={() => !isDisabled && handlePlaybackControlsClick(VIDEO_PLAYER_CONTROL_TYPES.SKIP_TO_LAST)} disabled={isDisabled}>
              <PlayerNext size={BUTTON_SIZE} />
            </IconButton>
          </ShortcutTooltip>}
      </div>

      <div className={css({
      width: FRAME_SLIDER_WIDTH
    })}>
        <FrameSlider maxFrames={maxFrames} sliderValue={sliderValue} isDisabled={!!isDisabled} handleSliderChange={handleSliderChange} handleSliderFinal={handleSliderFinal} />
      </div>

      <LabelSmall className={css({
      color: theme.colors.contentTertiary,
      whiteSpace: 'nowrap'
    })}>
        {sliderLabel}
      </LabelSmall>
    </div>;
};